import { createContext } from 'preact';
import { PropsWithChildren } from 'preact/compat';
import { useCallback, useContext, useEffect, useState } from 'preact/hooks';

interface Modal {
  id: string;
  closeModal: () => void;
  priority: number;
}

interface ModalTrackerContext {
  addModal: (modal: Modal) => void;
  removeModal: (id: string) => void;
}

const ModalTrackerContext = createContext<ModalTrackerContext | null>(null);

export function useModalTracker() {
  const context = useContext(ModalTrackerContext);

  if (context === null) {
    throw new Error('Webtex Reviews: No modal context was provided.');
  }

  return context;
}

export function ModalsTrackerProvider({
  children,
}: PropsWithChildren<unknown>) {
  const [modals, setModals] = useState<Modal[]>([]);

  useEffect(() => {
    function keyboardListener(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        let maxPriority = 0;

        modals.forEach((modal) => {
          if (modal.priority > maxPriority) {
            maxPriority = modal.priority;
          }
        });

        modals.forEach((modal) => {
          if (modal.priority === maxPriority) {
            modal.closeModal();
          }
        });
      }
    }

    if (modals.length === 0) {
      document.body.removeAttribute('data-wbtxr-noscroll');
    } else {
      document.body.setAttribute('data-wbtxr-noscroll', '');
    }

    window.addEventListener('keydown', keyboardListener);

    return () => {
      window.removeEventListener('keydown', keyboardListener);
    };
  }, [modals]);

  const addModal = useCallback(
    (newModal: Modal) => {
      if (!modals.find((modal) => modal.id === newModal.id)) {
        setModals((modals) => [...modals, newModal]);
      }
    },
    [modals],
  );

  const removeModal = useCallback(
    (id: string) => {
      const index = modals.findIndex((modal) => id === modal.id);

      if (index !== -1) {
        const modalsCopy = [...modals];

        modalsCopy.splice(index, 1);

        setModals(modalsCopy);
      }
    },
    [modals],
  );

  const contextValue: ModalTrackerContext = {
    addModal,
    removeModal,
  };

  return (
    <ModalTrackerContext.Provider value={contextValue}>
      {children}
    </ModalTrackerContext.Provider>
  );
}
